import { useCallback } from 'react';
import { useQueryString } from '@carafe/components';
import { SignInData } from '@account';
import { isEVineyardTarget } from '@carafe/models';
import { Routes } from '@routes';
import { useHistory } from 'react-router-dom';

const goUrlBase = process.env.GO_VINTRACE_URL;
const sandboxUrlBase = process.env.SANDBOX_VINTRACE_URL;
const eVineyardUrlBase = process.env.EVINEYARD_URL;

export const useHandleRedirect = (
  _allowDashboardRedirection = true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setToken: (token: string) => void = () => {},
) => {
  const history = useHistory();

  const [
    returnTo,
    customerCode,
    clientId,
    redirectUri,
    sandboxManagement,
    target,
  ] = useQueryString([
    'returnTo',
    'customerCode',
    'clientId',
    'redirectUri',
    'sandboxManagement',
    'target',
  ]);

  const handleRedirect = useCallback(
    (res: SignInData | Partial<SignInData>) => {
      const cc = res.customerCode || customerCode;

      console.log('handleRedirect called with:', {
        res,
        cc,
        customerCode,
        sandboxManagement,
        target,
      });

      if (clientId && redirectUri && res.token && res.apiUrl) {
        window.location.href = `${redirectUri}?token=${res.token}&apiUrl=${res.apiUrl}&customerCode=${cc}`;
        return;
      }

      // If we don't have a customerCode or tokenId, we ship the customer to the dashboard
      if (_allowDashboardRedirection && (!cc || !res.tokenId)) {
        setToken(res.token as string);
        history.push(Routes.Dashboard);
        return;
      }

      // If we have a customerCode and tokenId, we ship the customer off to either
      // sandbox management or the actual instance
      let goingTo: URL;
      if (isEVineyardTarget(target)) {
        goingTo = new URL(`${eVineyardUrlBase}/car/sso`);
      } else if (sandboxManagement?.toLowerCase() === 'true') {
        goingTo = new URL(`${sandboxUrlBase}`);
        goingTo.pathname = `manage/${cc}/sso`;
      } else {
        goingTo = new URL(`${goUrlBase}`);
        goingTo.pathname = `${cc}/sso`;
      }

      goingTo.searchParams.append(`code`, res.tokenId as string);

      // Only adding a returnTo when available.
      if (returnTo) {
        goingTo.searchParams.append(`returnTo`, returnTo);
      }

      console.log('handleRedirect redirecting to:', goingTo.toString());

      window.location.href = goingTo.toString();
    },
    [
      _allowDashboardRedirection,
      clientId,
      customerCode,
      history,
      redirectUri,
      returnTo,
      sandboxManagement,
      setToken,
      target,
    ],
  );

  return { handleRedirect };
};
